// Single source of truth for the HR Creations user manual (panel view + PDF).
export const MANUAL = {
  es: {
    title: "Manual de Usuario — HR Creations",
    subtitle: "Guía del Panel de Administración · Desde 1990",
    intro:
      "Este manual explica, paso a paso, cómo administrar su sitio: crear eventos, subir fotos y videos, proteger galerías, invitar personas, editar fotos en el Photo Lab, revisar duplicados y publicar restauraciones y paquetes de impresión.",
    sections: [
      {
        id: "acceso",
        title: "1. Entrar al Panel",
        steps: [
          "Abra el sitio y pulse “Mi Cuenta” en el menú.",
          "Escriba su correo y contraseña de administrador y pulse Entrar.",
          "Pulse “Panel de Administración” en el menú. Solo usted (rol admin) lo ve.",
          "Todo el panel funciona en español e inglés: cambie con ES | EN arriba a la derecha.",
        ],
      },
      {
        id: "resumen",
        title: "2. Resumen",
        steps: [
          "Muestra totales: eventos, álbumes, fotos, usuarios, espacio usado y descargas recientes.",
          "Úselo para confirmar que una subida entró: el número de fotos debe aumentar.",
        ],
      },
      {
        id: "crear-evento",
        title: "3. Crear un Evento",
        steps: [
          "Pestaña “Crear Evento”: escriba el nombre en español (el inglés se copia si lo deja vacío).",
          "Indique fecha, lugar y una descripción corta.",
          "Marque “Publicado” cuando quiera que aparezca en el sitio.",
          "Guarde. Después podrá añadir álbumes, portada, contraseña y videos.",
        ],
      },
      {
        id: "eventos",
        title: "4. Eventos: álbumes, portada, contraseña e invitaciones",
        steps: [
          "Pestaña “Eventos”: pulse un evento para abrir sus opciones.",
          "Álbumes: cree las categorías (Recepción, Parejas y Amigos, etc.). El nombre se guarda solo al escribir.",
          "Portada: suba una imagen desde su computadora o elija una foto del evento. Use “Encuadre” para mover la imagen y que no corte las caras.",
          "Contraseña: active la protección y escriba la contraseña que dará a los invitados. Sin ella nadie ve las fotos ni los videos del evento.",
          "Invitaciones: envíe enlaces personales por correo o WhatsApp. Cada enlace da acceso por 30 días sin necesidad de contraseña.",
          "Compartir: use el bloque de compartir para copiar el enlace de la galería o enviarlo por WhatsApp y Facebook.",
        ],
      },
      {
        id: "subir",
        title: "5. Subir Fotos",
        steps: [
          "Pestaña “Subir”: elija el evento y el álbum de destino.",
          "Arrastre las fotos o pulse para seleccionarlas. Se suben una por una y verá el progreso.",
          "Si una foto ya existe en el sitio (idéntica o la misma imagen exportada de nuevo), el sistema la omite y se lo avisa.",
          "Consejo: suba tandas de 10 a 20 fotos para que el progreso sea claro y evitar esperas largas.",
        ],
      },
      {
        id: "videos",
        title: "6. Videos",
        steps: [
          "Pestaña “Videos”: pegue el enlace de YouTube y elija el evento.",
          "En galerías con contraseña, la tarjeta del video solo se ve al entrar con acceso.",
          "Importante: quién puede ver el video en YouTube lo controla la privacidad que usted puso en YouTube.",
        ],
      },
      {
        id: "portafolio",
        title: "7. Portafolio y Portada del sitio",
        steps: [
          "Pestaña “Portafolio y Portada”: cambie las imágenes del inicio, de servicios y del portafolio.",
          "Suba su foto, elija la categoría y use “Encuadre” para centrar lo importante.",
          "Las fotos verticales se encuadran arriba por defecto para que se vean las caras.",
        ],
      },
      {
        id: "coleccion",
        title: "8. Álbumes de Colección",
        steps: [
          "Cree cada álbum en venta: título, tema, año, cantidad de fotos, formato (impreso, digital o ambos) y notas.",
          "Suba una portada; sin portada el álbum se ve vacío en el sitio.",
          "Desmarque “Disponible” para esconderlo sin borrarlo.",
        ],
      },
      {
        id: "restauraciones",
        title: "9. Restauraciones (Antes y Después)",
        steps: [
          "Pestaña “Restauraciones”: suba la foto ANTES y la foto DESPUÉS del mismo trabajo.",
          "Escriba el título y una nota corta; el año es opcional.",
          "Elija la orientación (vertical, horizontal o cuadrada) para que no se recorte.",
          "Al tener ambas fotos el ejemplo se publica automáticamente en la página “Restauración de Fotos”.",
          "Cada ejemplo tiene botones para compartir por WhatsApp y Facebook.",
        ],
      },
      {
        id: "photolab",
        title: "10. Photo Lab (editar fotos)",
        steps: [
          "Dos modos: “Fotos del álbum” (fotos ya publicadas) y “Desde mi PC” (hasta 20 fotos temporales que nadie ve hasta que usted las publique).",
          "Ajustes: balance de blancos automático, temperatura, tinte, exposición, contraste, saturación, nitidez, blanco y negro y girar 90°.",
          "Aplicar a esta foto: guarda los cambios en la foto activa. Aplicar a la selección: aplica los mismos ajustes a varias fotos a la vez.",
          "Zoom: “Acercar”, “Alejar” y “Ajustar a la pantalla” (vuelve al 100%). Con zoom puede desplazarse por la foto.",
          "Quitar brillo de lentes: active el modo, pulse encima de cada reflejo y elija la fuerza (suave, medio, fuerte). “Deshacer” quita el último punto.",
          "Tamaño de impresión: elija 2x2, 4x6, 5x7, 8x10, 11x14 o 20x30 y Vertical u Horizontal. Sin arrastrar, recorta centrado y sirve para varias fotos; arrastrando sobre la foto ajusta el recorte a esa proporción (una foto).",
          "Vista con marco: muestra cómo quedará enmarcada (negro, madera o dorado) con o sin paspartú. Es solo vista previa, no cambia la foto.",
          "Descargar editada: guarda la foto en su computadora; el nombre incluye el tamaño elegido (por ejemplo foto-editada-8x10.jpg).",
          "En “Desde mi PC”: elija evento y álbum y pulse “Publicar en el álbum”. Al publicarse, las fotos temporales desaparecen del laboratorio.",
          "Revertir: devuelve una foto del álbum a su versión original.",
        ],
      },
      {
        id: "duplicados",
        title: "11. Duplicados",
        steps: [
          "Pestaña “Duplicados”: muestra grupos de fotos repetidas, tanto copias idénticas como la misma imagen exportada de nuevo (por ejemplo .jpg y .png).",
          "Revise cada grupo y decida cuál copia conserva; borre las demás.",
          "Los videos de YouTube no se comparan porque no tienen archivo en el sitio.",
        ],
      },
      {
        id: "fotos",
        title: "12. Medios (biblioteca de fotos)",
        steps: [
          "Pestaña “Medios”: aquí se ven todas las fotos y videos del sitio, con filtros y buscador.",
          "Para borrar fotos de archivo (stock): filtre por Evento y luego por Álbum, o escriba el nombre en el buscador (por ejemplo “momentos-espontaneos” o “photo-booth”).",
          "Marque la casilla de cada foto que quiere quitar, o pulse “Seleccionar todas” si el filtro ya muestra solo esas.",
          "Pulse el botón rojo “Eliminar”. Sus fotos de cámara no corren riesgo: se llaman tipo XT2B1234.JPG o IMG_1234.jpg.",
          "En esta misma pestaña puede mover fotos a otro álbum, publicarlas o dejarlas en borrador, y activar o quitar descargas y marca de agua.",
        ],
      },
      {
        id: "envios",
        title: "13. Envíos y Usuarios",
        steps: [
          "Envíos: fotos que mandan los invitados; apruébelas para que entren al álbum o recháselas.",
          "Usuarios: vea las cuentas registradas y sus roles (admin, cliente, contribuidor).",
        ],
      },
      {
        id: "paquetes",
        title: "14. Paquetes de Fotos (pedidos por WhatsApp)",
        steps: [
          "Página pública “Paquetes de Fotos”: el cliente elige orientación, tamaños y cantidades.",
          "Al pulsar “Pedir por WhatsApp” le llega a usted el mensaje ya escrito con los tamaños y cantidades.",
          "Los precios se administran en Panel → Precios Futuros → “Precios de impresión”: escriba el precio de cada tamaño, elija “Desde $X” o “Precio exacto” y active “Mostrar precios”. Mientras esté apagado la página dice “cotización por WhatsApp”.",
          "Con los precios activos, el pedido de WhatsApp llega con el precio de cada tamaño y el total estimado.",
        ],
      },
      {
        id: "google",
        title: "15. Registrar el sitio en Google (una vez por dominio)",
        steps: [
          "Hágalo cuando el sitio ya esté publicado con su dominio propio y con el contenido real puesto. No lo haga con las direcciones de vista previa (…preview.emergentagent.com).",
          "1) Entre a search.google.com/search-console con su cuenta de Google y pulse “Añadir propiedad”.",
          "2) Elija el lado izquierdo, “Dominio”, y escriba solo el dominio: por ejemplo hrcreationsphoto.com (sin https:// ni www). Pulse Continuar.",
          "3) Google le dará un texto tipo “google-site-verification=…”. Cópielo.",
          "4) En Namecheap (o su proveedor): su dominio → Advanced DNS → Add New Record → Type: TXT Record, Host: @, Value: el texto copiado, TTL: Automatic. Guarde.",
          "5) Espere de 5 minutos a 1 hora y pulse VERIFY en Search Console. Nunca borre ese registro TXT: si lo quita, pierde la verificación.",
          "6) Menú izquierdo → Sitemaps → escriba la dirección completa: https://sudominio.com/sitemap.xml → Enviar. Si la propiedad es de tipo “Dominio”, hay que poner la dirección completa; si pone solo “sitemap.xml” dará “Invalid sitemap address”.",
          "7) Arriba, en “Inspeccionar cualquier URL”, pegue la portada y las páginas principales, una por una, y pulse “Solicitar indexación”.",
          "Es normal que al inicio diga “URL is unknown to Google”: significa que Google aún no ha visitado el sitio. En 1 a 3 días debe cambiar a “URL is on Google”.",
          "No hay que repetir nada en cada publicación (deploy) ni al subir fotos, eventos o álbumes. Solo solicite indexación cuando cree una página nueva o cambie mucho el texto de una existente.",
          "Recomendado además: cree su ficha gratis en business.google.com (Google Business Profile) para aparecer en Google Maps y en búsquedas como “fotógrafo en su pueblo”, y ponga el enlace del sitio en Facebook e Instagram.",
        ],
      },
      {
        id: "consejos",
        title: "16. Consejos y solución de problemas",
        steps: [
          "Si una subida se detiene, espere a que termine la foto en curso; suba tandas más pequeñas si su internet es lento.",
          "Si “Publicar en el álbum” no responde, revise que arriba haya un evento y un álbum elegidos.",
          "Si una galería se ve vacía para un invitado, confirme que el evento está Publicado y que le dio la contraseña o el enlace personal.",
          "Guarde su contraseña de administrador en un lugar seguro; con ella se ve todo el sitio sin restricciones.",
        ],
      },
    ],
  },
  en: {
    title: "User Manual — HR Creations",
    subtitle: "Admin Panel guide · Since 1990",
    intro:
      "This manual explains, step by step, how to run your site: create events, upload photos and videos, protect galleries, invite people, edit photos in Photo Lab, review duplicates, and publish restorations and print packages.",
    sections: [
      {
        id: "access",
        title: "1. Signing in",
        steps: [
          "Open the site and click “My Account” in the menu.",
          "Enter your admin email and password and sign in.",
          "Click “Admin Dashboard” in the menu. Only the admin role sees it.",
          "The whole panel is bilingual: switch with ES | EN at the top right.",
        ],
      },
      {
        id: "overview",
        title: "2. Overview",
        steps: [
          "Shows totals: events, albums, photos, users, storage used and recent downloads.",
          "Use it to confirm an upload landed: the photo count should go up.",
        ],
      },
      {
        id: "create-event",
        title: "3. Creating an event",
        steps: [
          "“Create Event” tab: type the Spanish name (English is copied if left blank).",
          "Add date, venue and a short description.",
          "Check “Published” when you want it visible on the site.",
          "Save. Then add albums, cover, password and videos.",
        ],
      },
      {
        id: "events",
        title: "4. Events: albums, cover, password and invitations",
        steps: [
          "“Events” tab: click an event to open its options.",
          "Albums: create the categories (Reception, Couples & Friends, etc.). Names save automatically as you type.",
          "Cover: upload an image or pick a photo from the event. Use “Framing” so faces are not cut off.",
          "Password: turn protection on and set the password you give guests. Without it nobody sees the photos or videos.",
          "Invitations: send personal links by email or WhatsApp. Each link grants 30 days of access with no password.",
          "Sharing: use the share block to copy the gallery link or send it over WhatsApp and Facebook.",
        ],
      },
      {
        id: "upload",
        title: "5. Uploading photos",
        steps: [
          "“Upload” tab: choose the destination event and album.",
          "Drag the photos or click to pick them. They upload one by one with progress.",
          "If a photo already exists on the site (identical or the same image re-exported), it is skipped and you are told.",
          "Tip: upload batches of 10–20 photos for clear progress.",
        ],
      },
      {
        id: "videos",
        title: "6. Videos",
        steps: [
          "“Videos” tab: paste the YouTube link and choose the event.",
          "In password-protected galleries the video card only appears after access is granted.",
          "Important: who can watch on YouTube depends on the privacy you set in YouTube.",
        ],
      },
      {
        id: "portfolio",
        title: "7. Portfolio and site images",
        steps: [
          "“Portfolio & Cover” tab: change the home, services and portfolio images.",
          "Upload your photo, choose the category and use “Framing” to center what matters.",
          "Portrait photos are framed from the top by default so faces stay visible.",
        ],
      },
      {
        id: "collection",
        title: "8. Collection albums",
        steps: [
          "Create each album for sale: title, theme, year, photo count, format (print, digital or both) and notes.",
          "Upload a cover; without one the album looks empty on the site.",
          "Uncheck “Available” to hide it without deleting it.",
        ],
      },
      {
        id: "restorations",
        title: "9. Restorations (Before & After)",
        steps: [
          "“Restorations” tab: upload the BEFORE and AFTER photos of the same job.",
          "Add a title and short note; the year is optional.",
          "Pick the orientation (portrait, landscape or square) so nothing is cropped.",
          "Once both photos exist the example publishes automatically on the Photo Restoration page.",
          "Each example has WhatsApp and Facebook share buttons.",
        ],
      },
      {
        id: "photolab",
        title: "10. Photo Lab (editing)",
        steps: [
          "Two modes: “Album photos” (already published) and “From my PC” (up to 20 temporary photos nobody sees until you publish them).",
          "Adjustments: auto white balance, temperature, tint, exposure, contrast, saturation, sharpness, black & white and 90° rotation.",
          "Apply to this photo saves the active photo. Apply to selection applies the same settings to several photos at once.",
          "Zoom: “Zoom in”, “Zoom out” and “Fit to screen” (back to 100%). While zoomed you can scroll around the photo.",
          "Remove glasses glare: turn the mode on, click each reflection and pick the strength (soft, medium, strong). “Undo” removes the last point.",
          "Print size: pick 2x2, 4x6, 5x7, 8x10, 11x14 or 20x30 plus Portrait or Landscape. Without dragging it center-crops and works for several photos; dragging sets the crop at that ratio (one photo).",
          "Frame preview: see how it will look framed (black, wood or gold) with or without a mat. Preview only, the photo is unchanged.",
          "Download edited: saves the photo to your computer; the filename includes the size (e.g. photo-editada-8x10.jpg).",
          "In “From my PC”: choose event and album, then click “Publish to album”. Published photos leave the lab automatically.",
          "Revert: returns an album photo to its original version.",
        ],
      },
      {
        id: "duplicates",
        title: "11. Duplicates",
        steps: [
          "“Duplicates” tab: shows groups of repeated photos — identical copies and the same image re-exported (e.g. .jpg and .png).",
          "Review each group, decide which copy to keep and delete the rest.",
          "YouTube videos are not compared because they have no file on the site.",
        ],
      },
      {
        id: "media",
        title: "12. Media (photo library)",
        steps: [
          "“Media” tab: every photo and video on the site, with filters and a search box.",
          "To delete stock photos: filter by Event then Album, or type the filename in the search box (for example “momentos-espontaneos” or “photo-booth”).",
          "Tick the checkbox on each photo you want gone, or click “Select all” when the filter already shows only those.",
          "Click the red “Delete” button. Your camera photos are safe: they are named like XT2B1234.JPG or IMG_1234.jpg.",
          "From the same tab you can move photos to another album, publish or unpublish them, and turn downloads or watermarks on and off.",
        ],
      },
      {
        id: "submissions",
        title: "13. Submissions and users",
        steps: [
          "Submissions: photos sent by guests; approve them into the album or reject them.",
          "Users: see registered accounts and their roles (admin, client, contributor).",
        ],
      },
      {
        id: "packages",
        title: "14. Photo Packages (WhatsApp orders)",
        steps: [
          "Public “Photo Packages” page: the client picks orientation, sizes and quantities.",
          "“Order on WhatsApp” sends you a ready-written message with the sizes and quantities.",
          "Prices live in Panel → Future Pricing → “Print prices”: type the price for each size, pick “From $X” or “Exact price” and switch on “Show prices”. While it is off the page says quote over WhatsApp.",
          "With prices on, the WhatsApp order arrives with the price per size and the estimated total.",
        ],
      },
      {
        id: "google",
        title: "15. Registering the site with Google (once per domain)",
        steps: [
          "Do this once the site is live on its own domain with the real content in place. Never do it with preview addresses (…preview.emergentagent.com).",
          "1) Go to search.google.com/search-console with your Google account and click “Add property”.",
          "2) Pick the left option, “Domain”, and type just the domain, e.g. hrcreationsphoto.com (no https:// and no www). Click Continue.",
          "3) Google gives you a line like “google-site-verification=…”. Copy it.",
          "4) In Namecheap (or your provider): your domain → Advanced DNS → Add New Record → Type: TXT Record, Host: @, Value: the copied line, TTL: Automatic. Save.",
          "5) Wait 5 minutes to 1 hour and click VERIFY. Never delete that TXT record or you lose verification.",
          "6) Left menu → Sitemaps → enter the full address: https://yourdomain.com/sitemap.xml → Submit. With a “Domain” property the full address is required; typing only “sitemap.xml” returns “Invalid sitemap address”.",
          "7) At the top, in “Inspect any URL”, paste the homepage and your main pages one by one and click “Request indexing”.",
          "“URL is unknown to Google” at the start is normal — Google has not visited yet. It should turn into “URL is on Google” within 1 to 3 days.",
          "Nothing needs repeating on each deploy or when uploading photos, events or albums. Only request indexing when you add a new page or heavily change an existing one.",
          "Also recommended: create your free listing at business.google.com (Google Business Profile) to show up on Google Maps and in searches like “photographer near me”, and add the site link to Facebook and Instagram.",
        ],
      },
      {
        id: "tips",
        title: "16. Tips and troubleshooting",
        steps: [
          "If an upload seems stuck, let the current photo finish; use smaller batches on slow connections.",
          "If “Publish to album” does nothing, check that an event and album are selected above.",
          "If a gallery looks empty to a guest, confirm the event is Published and that you gave them the password or personal link.",
          "Keep your admin password safe; it opens the entire site without restrictions.",
        ],
      },
    ],
  },
};

export const CLIENT_GUIDE = {
  es: {
    title: "Guía para Clientes",
    subtitle: "Cómo ver su galería y pedir sus fotos · HR Creations",
    intro:
      "En pocos pasos: entre a su galería privada, vea las fotos y videos, guarde sus favoritas, descárguelas y pida sus impresiones por WhatsApp.",
    sections: [
      {
        id: "entrar",
        title: "Entrar a su galería",
        steps: [
          "Abra el enlace que le enviamos por WhatsApp o correo. Si recibió un enlace personal, entra directo sin contraseña (válido por 30 días).",
          "Si su galería pide contraseña, escríbala tal como se la enviamos (respete mayúsculas y guiones) y pulse Entrar.",
          "También puede ir al menú “Eventos”, elegir su evento y escribir la contraseña ahí.",
          "Si le da error, revise que no haya espacios al final y pídanos el enlace de nuevo por WhatsApp.",
        ],
      },
      {
        id: "ver",
        title: "Ver fotos y videos",
        steps: [
          "Dentro de la galería verá los álbumes por categoría (por ejemplo Recepción, Parejas y Amigos).",
          "Pulse un álbum y luego una foto para verla grande; use las flechas para pasar de una a otra.",
          "Los videos aparecen como tarjetas: al pulsarlos se abren en YouTube en una pestaña nueva.",
        ],
      },
      {
        id: "favoritas",
        title: "Guardar sus favoritas",
        steps: [
          "Cree su cuenta o entre en “Mi Cuenta” para poder guardar favoritas.",
          "Pulse el corazón en cada foto que quiera separar.",
          "En “Mi Cuenta” verá su lista de favoritas: es la forma más fácil de decirnos cuáles quiere impresas.",
        ],
      },
      {
        id: "descargar",
        title: "Descargar fotos",
        steps: [
          "Si el evento permite descargas, verá el botón de descarga en la foto o en el álbum.",
          "En el teléfono, la foto se guarda en su carrete o en la carpeta de descargas.",
          "Si no ve el botón, escríbanos por WhatsApp y le enviamos los archivos que necesite.",
        ],
      },
      {
        id: "pedir",
        title: "Pedir impresiones",
        steps: [
          "Entre a la página “Paquetes de Fotos” del sitio.",
          "Elija la orientación (vertical u horizontal), los tamaños (2x2, 4x6, 5x7, 8x10, 11x14, 20x30) y la cantidad de cada uno.",
          "Pulse “Pedir por WhatsApp”: se abre el mensaje ya escrito con su pedido; solo envíelo.",
          "Díganos el nombre del evento y cuáles fotos (o su lista de favoritas) y le confirmamos precio y tiempo de entrega.",
          "Hay marcos disponibles en negro, madera y dorado. Pídanos una muestra enmarcada si desea verla antes.",
        ],
      },
      {
        id: "ayuda",
        title: "¿Necesita ayuda?",
        steps: [
          "WhatsApp: 1 (787) 938-8834",
          "Correo: hrcreations90@gmail.com",
          "Sus fotos son privadas: no comparta la contraseña de la galería fuera de su grupo.",
        ],
      },
    ],
  },
  en: {
    title: "Client Guide",
    subtitle: "How to view your gallery and order your photos · HR Creations",
    intro:
      "In a few steps: open your private gallery, browse photos and videos, save favorites, download them and order prints over WhatsApp.",
    sections: [
      {
        id: "access",
        title: "Opening your gallery",
        steps: [
          "Open the link we sent by WhatsApp or email. A personal link lets you in with no password (valid for 30 days).",
          "If your gallery asks for a password, type it exactly as we sent it (mind capitals and dashes) and continue.",
          "You can also go to “Events”, pick your event and enter the password there.",
          "If it fails, check for extra spaces at the end and ask us for a fresh link on WhatsApp.",
        ],
      },
      {
        id: "browse",
        title: "Photos and videos",
        steps: [
          "Inside the gallery you will see albums by category (Reception, Couples & Friends, and so on).",
          "Open an album, then a photo to see it large; use the arrows to move through them.",
          "Videos appear as cards and open on YouTube in a new tab.",
        ],
      },
      {
        id: "favorites",
        title: "Saving favorites",
        steps: [
          "Create an account or sign in under “My Account” to save favorites.",
          "Tap the heart on every photo you want to set aside.",
          "Your favorites list in “My Account” is the easiest way to tell us which photos to print.",
        ],
      },
      {
        id: "download",
        title: "Downloading photos",
        steps: [
          "If the event allows downloads, you will see the download button on the photo or album.",
          "On a phone the photo is saved to your camera roll or downloads folder.",
          "If you do not see the button, message us on WhatsApp and we will send the files you need.",
        ],
      },
      {
        id: "order",
        title: "Ordering prints",
        steps: [
          "Open the “Photo Packages” page on the site.",
          "Pick the orientation (portrait or landscape), the sizes (2x2, 4x6, 5x7, 8x10, 11x14, 20x30) and how many of each.",
          "Tap “Order on WhatsApp”: the message is written for you — just send it.",
          "Tell us the event name and which photos (or your favorites list) and we confirm price and delivery time.",
          "Frames are available in black, wood and gold. Ask us for a framed sample if you want to see it first.",
        ],
      },
      {
        id: "help",
        title: "Need help?",
        steps: [
          "WhatsApp: 1 (787) 938-8834",
          "Email: hrcreations90@gmail.com",
          "Your photos are private: please do not share the gallery password outside your group.",
        ],
      },
    ],
  },
};
